'use client';

import React, { useState, useRef, useEffect } from 'react';
import WardleyMap, { type WardleyMapData, type WardleyComponent, type Annotation, type EvolutionStage } from './WardleyMap';

interface BuilderComponent extends WardleyComponent {
  id: string;
}

const DEFAULT_EVOLUTION_STAGES: EvolutionStage[] = [
  { name: 'Genesis', range: [0, 0.25], description: 'Novel, uncertain' },
  { name: 'Custom Built', range: [0.25, 0.50], description: 'Understood but not standardized' },
  { name: 'Product', range: [0.50, 0.75], description: 'Productized solutions available' },
  { name: 'Commodity', range: [0.75, 1.0], description: 'Standardized, utility-like' },
];

const HELP_CONTENT: Record<string, { title: string; content: string }> = {
  'axis-editor': {
    title: 'Axis Labels & Evolution Stages',
    content: `**Customize Your Map Axes:**

**X-Axis (Horizontal):** Represents evolution or maturity of components. Default is "Evolution" but you can change it to "Maturity", "Time", or any custom label that fits your mapping context.

**Y-Axis (Vertical):** Represents visibility or value chain position. Default is "Visibility" but you can use "Value Chain", "Importance", or any custom label.

**Evolution Stages:** Customize the four evolution stage names (e.g., Genesis → Custom Built → Product → Commodity). These stages help you categorize how evolved each component is.

**Pro Tip:** For non-technical maps, try using "User Needs" (Y-axis) and "Maturity" (X-axis) for better clarity.`,
  },
  'export': {
    title: 'Export Your Map',
    content: `**Export Formats:**

**Markdown:** Export as markdown text that can be pasted into documentation, GitHub, or blog posts. Includes component list and dependencies in a readable format.

**JSON:** Export as structured JSON data that can be imported later or used programmatically. Preserves all map details including positions, dependencies, and annotations.

**PNG Image:** Download a PNG screenshot of your map for presentations, reports, or sharing on social media.

**Pro Tip:** Use JSON for backups and versioning, use PNG for presentations, and use Markdown for documentation.`,
  },
  'import': {
    title: 'Import a Map',
    content: `**Import from JSON:**

Paste a JSON export from a previous map to restore it. This is useful for:
- Loading backups
- Sharing maps with team members
- Versioning your strategic maps

**How to Use:**
1. Click the "Import" button in the toolbar
2. Paste the JSON data (exported from this tool)
3. Click "Import" to load the map

**Pro Tip:** Save your JSON exports in version control (Git) to track how your strategy evolves over time.`,
  },
  'examples': {
    title: 'Example Maps',
    content: `**Learn by Example:**

Load pre-built example maps to understand Wardley mapping concepts:

**Platform Engineering Evolution:** Shows how platform teams evolve from custom scripts to self-service platforms.

**Observability Stack:** Demonstrates monitoring and alerting evolution from basic logs to modern observability.

**Authentication System:** Maps the evolution of auth from custom solutions to managed services.

**Pro Tip:** Load an example, explore the components and dependencies, then click "Clear Map" to start building your own based on what you learned.`,
  },
};

/**
 * Interactive Wardley Map Builder
 *
 * Allows users to create Wardley maps visually by clicking to add components,
 * drawing dependencies, and exporting to markdown/JSON.
 */
export default function WardleyMapBuilder() {
  const [title, setTitle] = useState('My Wardley Map');
  const [components, setComponents] = useState<BuilderComponent[]>([]);
  const [annotations, setAnnotations] = useState<Annotation[]>([]);
  const [mode, setMode] = useState<'add' | 'connect' | 'annotate' | 'edit' | null>(null);
  const [selectedComponentId, setSelectedComponentId] = useState<string | null>(null);
  const [connectingFrom, setConnectingFrom] = useState<string | null>(null);
  const [showExport, setShowExport] = useState(false);
  const [exportFormat, setExportFormat] = useState<'markdown' | 'json'>('markdown');
  const [showAxisEditor, setShowAxisEditor] = useState(false);
  const [xAxisLabel, setXAxisLabel] = useState('Evolution');
  const [yAxisLabel, setYAxisLabel] = useState('Visibility');
  const [evolutionStages, setEvolutionStages] = useState<EvolutionStage[]>(DEFAULT_EVOLUTION_STAGES);
  const [showImport, setShowImport] = useState(false);
  const [importText, setImportText] = useState('');
  const [showExamples, setShowExamples] = useState(false);
  const [showHelp, setShowHelp] = useState<string | null>(null);

  const canvasRef = useRef<HTMLDivElement>(null);
  const [lastSaved, setLastSaved] = useState<Date | null>(null);
  const [showSaveIndicator, setShowSaveIndicator] = useState(false);

  // Context menu state
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; componentId?: string } | null>(null);
  const [clipboard, setClipboard] = useState<BuilderComponent | null>(null);

  // Undo/Redo state
  const [history, setHistory] = useState<BuilderComponent[][]>([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  // Load from localStorage on mount
  useEffect(() => {
    const saved = localStorage.getItem('wardley-map-builder-state');
    if (saved) {
      try {
        const state = JSON.parse(saved);
        setTitle(state.title || 'My Wardley Map');
        setComponents(state.components || []);
        setAnnotations(state.annotations || []);
        setXAxisLabel(state.xAxisLabel || 'Evolution');
        setYAxisLabel(state.yAxisLabel || 'Visibility');
        setEvolutionStages(state.evolutionStages || DEFAULT_EVOLUTION_STAGES);
        setLastSaved(new Date(state.savedAt));
      } catch (e) {
        console.error('Failed to load saved state:', e);
      }
    }
  }, []);

  // Auto-save to localStorage every 30 seconds
  useEffect(() => {
    const saveInterval = setInterval(() => {
      const state = {
        title,
        components,
        annotations,
        xAxisLabel,
        yAxisLabel,
        evolutionStages,
        savedAt: new Date().toISOString(),
      };
      localStorage.setItem('wardley-map-builder-state', JSON.stringify(state));
      setLastSaved(new Date());
      setShowSaveIndicator(true);
      setTimeout(() => setShowSaveIndicator(false), 2000);
    }, 30000); // Every 30 seconds

    return () => clearInterval(saveInterval);
  }, [title, components, annotations, xAxisLabel, yAxisLabel, evolutionStages]);

  // Paste component (declared before keyboard shortcut handler)
  const handlePaste = () => {
    if (!clipboard) return;

    const newComponent: BuilderComponent = {
      ...clipboard,
      id: `component-${Date.now()}`,
      // Offset slightly from original position
      evolution: Math.min(clipboard.evolution + 0.05, 0.95),
      visibility: Math.min(clipboard.visibility + 0.05, 0.95),
    };

    setComponents([...components, newComponent]);
    setContextMenu(null);
  };

  // Undo (declared before keyboard shortcut handler)
  const handleUndo = () => {
    if (historyIndex > 0) {
      setHistoryIndex(historyIndex - 1);
      setComponents(history[historyIndex - 1]);
    }
  };

  // Redo (declared before keyboard shortcut handler)
  const handleRedo = () => {
    if (historyIndex < history.length - 1) {
      setHistoryIndex(historyIndex + 1);
      setComponents(history[historyIndex + 1]);
    }
  };

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // ESC: Cancel/deselect
      if (e.key === 'Escape') {
        setMode(null);
        setSelectedComponentId(null);
        setConnectingFrom(null);
        setContextMenu(null);
        return;
      }

      // Check if we're in an input field
      const target = e.target as HTMLElement;
      if (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA') {
        return; // Don't handle shortcuts when typing
      }

      const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
      const modifier = isMac ? e.metaKey : e.ctrlKey;

      // Ctrl/Cmd + Z: Undo
      if (modifier && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
        return;
      }

      // Ctrl/Cmd + Shift + Z or Ctrl/Cmd + Y: Redo
      if (modifier && ((e.key === 'z' && e.shiftKey) || e.key === 'y')) {
        e.preventDefault();
        handleRedo();
        return;
      }

      // Ctrl/Cmd + X: Cut (only if component selected)
      if (modifier && e.key === 'x') {
        e.preventDefault();
        if (selectedComponentId) {
          const component = components.find(c => c.id === selectedComponentId);
          if (component) {
            setClipboard(component);
            setComponents(components.filter(c => c.id !== selectedComponentId));
            setSelectedComponentId(null);
          }
        }
        return;
      }

      // Ctrl/Cmd + C: Copy (only if component selected)
      if (modifier && e.key === 'c') {
        e.preventDefault();
        if (selectedComponentId) {
          const component = components.find(c => c.id === selectedComponentId);
          if (component) {
            setClipboard(component);
          }
        }
        return;
      }

      // Ctrl/Cmd + V: Paste
      if (modifier && e.key === 'v') {
        e.preventDefault();
        handlePaste();
        return;
      }

      // Delete/Backspace: Delete selected component
      if ((e.key === 'Delete' || e.key === 'Backspace') && selectedComponentId) {
        e.preventDefault();
        setComponents(components.filter(c => c.id !== selectedComponentId));
        setSelectedComponentId(null);
        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedComponentId, components, clipboard, history, historyIndex]);

  // Toggle mode (click again to deselect)
  const toggleMode = (newMode: 'add' | 'connect' | 'annotate' | 'edit') => {
    if (mode === newMode) {
      setMode(null);
      setConnectingFrom(null);
    } else {
      setMode(newMode);
      setConnectingFrom(null);
    }
  };

  // Add component at clicked position
  const handleCanvasClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (mode !== 'add') return;

    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return;

    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;

    // Convert pixel coordinates to 0-1 scale
    const evolution = x / rect.width;
    const visibility = 1 - (y / rect.height); // Invert Y axis

    const newComponent: BuilderComponent = {
      id: `component-${Date.now()}`,
      name: `Component ${components.length + 1}`,
      visibility: Math.max(0, Math.min(1, visibility)),
      evolution: Math.max(0, Math.min(1, evolution)),
      description: '',
      dependencies: [],
    };

    setComponents([...components, newComponent]);
    setSelectedComponentId(newComponent.id);
  };

  // Handle component selection for connecting
  const handleComponentClick = (componentId: string) => {
    if (mode === 'connect') {
      if (!connectingFrom) {
        setConnectingFrom(componentId);
      } else {
        // Add dependency
        setComponents(components.map(c =>
          c.id === connectingFrom
            ? { ...c, dependencies: [...(c.dependencies || []), components.find(comp => comp.id === componentId)!.name] }
            : c
        ));
        setConnectingFrom(null);
      }
    } else if (mode === 'edit') {
      setSelectedComponentId(componentId);
    }
  };

  // Update selected component
  const updateComponent = (updates: Partial<BuilderComponent>) => {
    if (!selectedComponentId) return;

    setComponents(components.map(c =>
      c.id === selectedComponentId ? { ...c, ...updates } : c
    ));
  };

  // Delete selected component
  const deleteComponent = () => {
    if (!selectedComponentId) return;

    const componentName = components.find(c => c.id === selectedComponentId)?.name;

    // Remove component and its dependencies
    setComponents(components
      .filter(c => c.id !== selectedComponentId)
      .map(c => ({
        ...c,
        dependencies: (c.dependencies || []).filter(dep => dep !== componentName)
      }))
    );
    setSelectedComponentId(null);
  };

  // Generate markdown export
  const generateMarkdown = (): string => {
    const componentsYaml = components.map(c => `  - name: "${c.name}"
    visibility: ${c.visibility.toFixed(2)}
    evolution: ${c.evolution.toFixed(2)}${c.description ? `
    description: "${c.description}"` : ''}${c.dependencies && c.dependencies.length > 0 ? `
    dependencies: [${c.dependencies.map(d => `"${d}"`).join(', ')}]` : ''}${c.inertia ? `
    inertia: true` : ''}`).join('\n\n');

    const annotationsYaml = annotations.length > 0 ? `\n\nannotations:\n` + annotations.map(a => `  - position: [${a.position[0].toFixed(2)}, ${a.position[1].toFixed(2)}]
    text: "${a.text}"
    type: "${a.type}"`).join('\n\n') : '';

    return `---
title: "${title}"
date: "${new Date().toISOString().split('T')[0]}"
author: "Your Name"
excerpt: "Description of this Wardley map"
tags: ["strategy", "mapping"]
category: "wardley-maps"
featured: false
premium: false
components:
${componentsYaml}${annotationsYaml}
---

## Overview

[Add your analysis here]

## Key Insights

1. **Insight 1**: Description
2. **Insight 2**: Description
3. **Insight 3**: Description

## Strategic Recommendations

### Near-Term (Next 6 Months)

1. **Action 1**: Description
2. **Action 2**: Description

### Long-Term (12+ Months)

1. **Action 1**: Description
2. **Action 2**: Description
`;
  };

  // Generate JSON export
  const generateJSON = (): string => {
    const data: WardleyMapData = {
      title,
      components: components.map(({ id, ...c }) => c),
      annotations,
    };
    return JSON.stringify(data, null, 2);
  };

  // Export to file
  const handleExport = () => {
    const content = exportFormat === 'markdown' ? generateMarkdown() : generateJSON();
    const blob = new Blob([content], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.toLowerCase().replace(/\s+/g, '-')}.${exportFormat === 'markdown' ? 'md' : 'json'}`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export as PNG
  const handleExportPNG = () => {
    const canvas = document.querySelector('.wardley-map-builder canvas') as HTMLCanvasElement;
    if (!canvas) return;

    const url = canvas.toDataURL('image/png');
    const a = document.createElement('a');
    a.href = url;
    a.download = `${title.toLowerCase().replace(/\s+/g, '-')}.png`;
    a.click();
  };

  // Copy as PNG to clipboard
  const handleCopyAsImage = async () => {
    const canvas = document.querySelector('.wardley-map-builder canvas') as HTMLCanvasElement;
    if (!canvas) return;

    try {
      // Convert canvas to blob
      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(resolve, 'image/png');
      });

      if (blob) {
        // Copy to clipboard using ClipboardItem API
        await navigator.clipboard.write([
          new ClipboardItem({
            'image/png': blob
          })
        ]);
        alert('Map copied to clipboard as image!');
      }
    } catch (error) {
      console.error('Failed to copy image:', error);
      alert('Failed to copy image to clipboard. Try using "Export" > "PNG" instead.');
    }
    setContextMenu(null);
  };

  // Context menu handler
  const handleContextMenu = (e: React.MouseEvent<HTMLDivElement>, componentId?: string) => {
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      componentId
    });
  };

  // Close context menu on click outside
  useEffect(() => {
    const handleClick = () => setContextMenu(null);
    if (contextMenu) {
      window.addEventListener('click', handleClick);
      return () => window.removeEventListener('click', handleClick);
    }
  }, [contextMenu]);

  // Cut component
  const handleCut = () => {
    if (!contextMenu?.componentId) return;
    const component = components.find(c => c.id === contextMenu.componentId);
    if (component) {
      setClipboard(component);
      setComponents(components.filter(c => c.id !== contextMenu.componentId));
    }
    setContextMenu(null);
  };

  // Copy component
  const handleCopy = () => {
    if (!contextMenu?.componentId) return;
    const component = components.find(c => c.id === contextMenu.componentId);
    if (component) {
      setClipboard(component);
    }
    setContextMenu(null);
  };

  // Delete component
  const handleDeleteFromContext = () => {
    if (!contextMenu?.componentId) return;
    setComponents(components.filter(c => c.id !== contextMenu.componentId));
    setContextMenu(null);
  };

  // Track history when components change
  const updateComponentsWithHistory = (newComponents: BuilderComponent[]) => {
    // Add current state to history
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(components);
    setHistory(newHistory);
    setHistoryIndex(newHistory.length - 1);
    setComponents(newComponents);
  };

  // Import JSON
  const handleImport = () => {
    try {
      const data = JSON.parse(importText);
      if (data.title) setTitle(data.title);
      if (data.components) {
        setComponents(data.components.map((c: WardleyComponent, idx: number) => ({
          ...c,
          id: `component-${Date.now()}-${idx}`,
        })));
      }
      if (data.annotations) setAnnotations(data.annotations);
      if (data.xAxisLabel) setXAxisLabel(data.xAxisLabel);
      if (data.yAxisLabel) setYAxisLabel(data.yAxisLabel);
      setShowImport(false);
      setImportText('');
      alert('Map imported successfully!');
    } catch (e) {
      alert('Invalid JSON format. Please check your input.');
    }
  };

  // New map
  const handleNewMap = () => {
    if (components.length > 0 && !confirm('Start a new map? Your current work is auto-saved and you can reload it.')) {
      return;
    }
    setTitle('My Wardley Map');
    setComponents([]);
    setAnnotations([]);
    setXAxisLabel('Evolution');
    setYAxisLabel('Visibility');
    setMode(null);
    setSelectedComponentId(null);
    localStorage.removeItem('wardley-map-builder-state');
  };

  // Load example map
  const loadExample = async (example: string) => {
    const examples: { [key: string]: any } = {
      'platform-engineering': {
        title: 'Platform Engineering Evolution',
        xAxisLabel: 'Evolution',
        yAxisLabel: 'Visibility',
        components: [
          { name: 'Developer Velocity', visibility: 0.95, evolution: 0.60, description: 'Speed of shipping features' },
          { name: 'Self-Service Platform', visibility: 0.90, evolution: 0.55, description: 'Internal developer platform', dependencies: ['Platform APIs', 'Documentation Portal'] },
          { name: 'Platform APIs', visibility: 0.70, evolution: 0.65, description: 'Standardized APIs for common tasks' },
          { name: 'Documentation Portal', visibility: 0.65, evolution: 0.70, description: 'Developer documentation' },
          { name: 'Custom Scripts', visibility: 0.85, evolution: 0.10, description: 'Manual deployment scripts', inertia: true },
        ],
      },
      'observability': {
        title: 'Observability Stack Evolution',
        xAxisLabel: 'Evolution',
        yAxisLabel: 'Visibility',
        components: [
          { name: 'Service Reliability', visibility: 0.95, evolution: 0.70, description: 'End goal: reliable services' },
          { name: 'Alerting System', visibility: 0.80, evolution: 0.75, description: 'PagerDuty, Opsgenie', dependencies: ['Metrics Database'] },
          { name: 'Metrics Database', visibility: 0.60, evolution: 0.75, description: 'Prometheus, InfluxDB' },
          { name: 'Custom Scripts', visibility: 0.85, evolution: 0.15, description: 'Homegrown monitoring', inertia: true },
        ],
      },
      'authentication': {
        title: 'Authentication & Authorization',
        xAxisLabel: 'Evolution',
        yAxisLabel: 'Visibility',
        components: [
          { name: 'User Access', visibility: 0.95, evolution: 0.75, description: 'Users logging in' },
          { name: 'Single Sign-On', visibility: 0.90, evolution: 0.65, description: 'One login for all services', dependencies: ['Identity Provider'] },
          { name: 'Identity Provider', visibility: 0.60, evolution: 0.80, description: 'Okta, Azure AD' },
          { name: 'Custom Auth Code', visibility: 0.75, evolution: 0.10, description: 'Homegrown authentication', inertia: true },
        ],
      },
    };

    const exampleData = examples[example];
    if (exampleData) {
      setTitle(exampleData.title);
      setXAxisLabel(exampleData.xAxisLabel);
      setYAxisLabel(exampleData.yAxisLabel);
      setComponents(exampleData.components.map((c: WardleyComponent, idx: number) => ({
        ...c,
        id: `component-${Date.now()}-${idx}`,
      })));
      setAnnotations([]);
      setShowExamples(false);
    }
  };

  const selectedComponent = components.find(c => c.id === selectedComponentId);

  return (
    <div className="wardley-map-builder">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-2">
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="text-3xl font-bold bg-transparent border-b-2 border-border focus:border-accent outline-none text-foreground flex-1"
            placeholder="Map Title"
          />
          {showSaveIndicator && (
            <span className="ml-4 text-sm text-green-400 animate-fade-in">
              ✓ Auto-saved
            </span>
          )}
          {lastSaved && !showSaveIndicator && (
            <span className="ml-4 text-sm text-foreground-muted">
              Last saved: {lastSaved.toLocaleTimeString()}
            </span>
          )}
        </div>
        <p className="text-foreground-tertiary text-sm">
          Click to add components, connect them, and export your strategic map. Your work is auto-saved.
        </p>
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 mb-4">
        <button
          onClick={handleNewMap}
          className="px-4 py-2 rounded-lg bg-surface text-foreground hover:bg-surfaceer transition-colors"
        >
          📄 New
        </button>
        <button
          onClick={() => setShowExamples(!showExamples)}
          className="px-4 py-2 rounded-lg bg-surface text-foreground hover:bg-surfaceer transition-colors"
        >
          📚 Examples
        </button>
        <button
          onClick={() => setShowImport(!showImport)}
          className="px-4 py-2 rounded-lg bg-surface text-foreground hover:bg-surfaceer transition-colors"
        >
          📥 Import
        </button>
        <button
          onClick={() => toggleMode('add')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            mode === 'add'
              ? 'bg-accent text-accent-foreground'
              : 'bg-surface text-foreground hover:bg-surfaceer'
          }`}
        >
          ➕ Add Component
        </button>
        <button
          onClick={() => toggleMode('connect')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            mode === 'connect'
              ? 'bg-accent text-accent-foreground'
              : 'bg-surface text-foreground hover:bg-surfaceer'
          }`}
        >
          🔗 Connect
        </button>
        <button
          onClick={() => toggleMode('edit')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            mode === 'edit'
              ? 'bg-accent text-accent-foreground'
              : 'bg-surface text-foreground hover:bg-surfaceer'
          }`}
        >
          ✏️ Edit
        </button>
        <button
          onClick={() => setShowAxisEditor(!showAxisEditor)}
          className={`px-4 py-2 rounded-lg transition-colors ${
            showAxisEditor
              ? 'bg-accent text-accent-foreground'
              : 'bg-surface text-foreground hover:bg-surfaceer'
          }`}
        >
          📊 Axes
        </button>
        <button
          onClick={() => setShowExport(!showExport)}
          className="px-4 py-2 rounded-lg bg-surface text-foreground hover:bg-surfaceer transition-colors"
        >
          💾 Export
        </button>
        <button
          onClick={() => {
            if (confirm('Clear all components and start over?')) {
              setComponents([]);
              setAnnotations([]);
              setSelectedComponentId(null);
              setMode(null);
            }
          }}
          className="px-4 py-2 rounded-lg bg-red-900/20 text-red-400 hover:bg-red-900/30 transition-colors"
        >
          🗑️ Clear
        </button>
      </div>

      {/* Mode instructions */}
      <div className="mb-4 p-3 bg-surface rounded-lg text-sm text-foreground-secondary">
        {!mode && '💡 Select a mode above to get started (or press ESC to deselect)'}
        {mode === 'add' && '👆 Click anywhere on the canvas to add a new component (click mode again or press ESC to cancel)'}
        {mode === 'connect' && !connectingFrom && '🔗 Click two components to create a dependency (from → to) (press ESC to cancel)'}
        {mode === 'edit' && '✏️ Click a component to edit its properties'}
        {connectingFrom && (
          <span className="text-accent">
            🔗 Connecting from &quot;{components.find(c => c.id === connectingFrom)?.name}&quot;... click destination component (press ESC to cancel)
          </span>
        )}
      </div>

      {/* Axis Editor */}
      {showAxisEditor && (
        <div className="mb-4 p-4 bg-surface rounded-lg border border-border">
          <div className="flex items-center gap-2 mb-3">
            <h3 className="text-lg font-semibold text-accent">Axis Labels & Evolution Stages</h3>
            <button
              onClick={() => setShowHelp('axis-editor')}
              className="text-foreground-tertiary hover:text-accent transition-colors"
              title="Learn how to use this feature"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">X-Axis (Horizontal)</label>
              <input
                type="text"
                value={xAxisLabel}
                onChange={(e) => setXAxisLabel(e.target.value)}
                className="w-full px-3 py-2 bg-background rounded border border-border text-foreground focus:border-accent outline-none"
                placeholder="e.g., Evolution, Maturity, Time"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Y-Axis (Vertical)</label>
              <input
                type="text"
                value={yAxisLabel}
                onChange={(e) => setYAxisLabel(e.target.value)}
                className="w-full px-3 py-2 bg-background rounded border border-border text-foreground focus:border-accent outline-none"
                placeholder="e.g., Visibility, Value Chain, Importance"
              />
            </div>
          </div>

          <div className="border-t border-border-muted pt-4">
            <label className="block text-sm font-medium text-foreground mb-2">Evolution Stage Names (0% → 100%)</label>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
              {evolutionStages.map((stage, idx) => (
                <div key={idx}>
                  <input
                    type="text"
                    value={stage.name}
                    onChange={(e) => {
                      const newStages = [...evolutionStages];
                      newStages[idx] = { ...stage, name: e.target.value };
                      setEvolutionStages(newStages);
                    }}
                    className="w-full px-2 py-1 text-sm bg-background rounded border border-border text-foreground focus:border-accent outline-none"
                    placeholder={`Stage ${idx + 1}`}
                  />
                  <p className="text-xs text-foreground-muted mt-1">
                    {Math.round(stage.range[0] * 100)}%-{Math.round(stage.range[1] * 100)}%
                  </p>
                </div>
              ))}
            </div>
            <p className="text-xs text-foreground-tertiary mt-2">
              Customize stage names (e.g., &quot;Genesis → Custom → Product → Commodity&quot; or &quot;Idea → Build → Launch → Scale&quot;)
            </p>
          </div>

          <p className="text-xs text-foreground-tertiary mt-4">
            All changes appear on the map immediately and are auto-saved.
          </p>
        </div>
      )}

      {/* Main layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Canvas */}
        <div className="lg:col-span-2">
          <div
            ref={canvasRef}
            onClick={handleCanvasClick}
            onContextMenu={(e) => handleContextMenu(e)}
            className="relative cursor-crosshair"
          >
            <WardleyMap
              data={{
                title,
                components: components.map(({ id, ...c }) => c),
                annotations,
                xAxisLabel,
                yAxisLabel,
                evolution_stages: evolutionStages,
              }}
              width={800}
              height={600}
              interactive={false}
            />
            {/* Clickable overlays for components */}
            <div className="absolute inset-0 pointer-events-none">
              {components.map((component) => {
                const x = 60 + component.evolution * (800 - 120);
                const y = 40 + (1 - component.visibility) * (600 - 100);
                const isSelected = selectedComponentId === component.id;
                const isConnectingFrom = connectingFrom === component.id;
                return (
                  <div
                    key={component.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleComponentClick(component.id);
                    }}
                    onContextMenu={(e) => {
                      e.stopPropagation();
                      handleContextMenu(e, component.id);
                    }}
                    className={`absolute pointer-events-auto cursor-pointer transition-all ${
                      isSelected ? 'ring-2 ring-ring rounded-full' : ''
                    } ${isConnectingFrom ? 'ring-2 ring-blue-400 rounded-full animate-pulse' : ''}`}
                    style={{
                      left: `${x - 15}px`,
                      top: `${y - 15}px`,
                      width: '30px',
                      height: '30px',
                    }}
                    title={component.name}
                  />
                );
              })}
            </div>
          </div>

          <div className="mt-4 text-sm text-foreground-tertiary">
            <strong>Components:</strong> {components.length} |{' '}
            <strong>Dependencies:</strong> {components.reduce((sum, c) => sum + (c.dependencies?.length || 0), 0)}
          </div>
        </div>

        {/* Properties panel */}
        <div className="bg-surface rounded-lg p-4">
          {selectedComponent ? (
            <div>
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-accent">Edit Component</h3>
                <button
                  onClick={() => setSelectedComponentId(null)}
                  className="text-foreground-tertiary hover:text-foreground text-sm"
                >
                  ✕ Close
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Name</label>
                  <input
                    type="text"
                    value={selectedComponent.name}
                    onChange={(e) => updateComponent({ name: e.target.value })}
                    className="w-full px-3 py-2 bg-background rounded border border-border text-foreground focus:border-accent outline-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Description</label>
                  <textarea
                    value={selectedComponent.description || ''}
                    onChange={(e) => updateComponent({ description: e.target.value })}
                    className="w-full px-3 py-2 bg-background rounded border border-border text-foreground focus:border-accent outline-none h-20"
                    placeholder="Optional description"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Visibility: {(selectedComponent.visibility * 100).toFixed(0)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={selectedComponent.visibility * 100}
                    onChange={(e) => updateComponent({ visibility: Number(e.target.value) / 100 })}
                    className="w-full"
                  />
                  <p className="text-xs text-foreground-tertiary mt-1">How visible to users (top = high)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">
                    Evolution: {(selectedComponent.evolution * 100).toFixed(0)}%
                  </label>
                  <input
                    type="range"
                    min="0"
                    max="100"
                    value={selectedComponent.evolution * 100}
                    onChange={(e) => updateComponent({ evolution: Number(e.target.value) / 100 })}
                    className="w-full"
                  />
                  <p className="text-xs text-foreground-tertiary mt-1">Genesis → Custom → Product → Commodity</p>
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="inertia"
                    checked={selectedComponent.inertia || false}
                    onChange={(e) => updateComponent({ inertia: e.target.checked })}
                    className="w-4 h-4"
                  />
                  <label htmlFor="inertia" className="text-sm text-foreground">
                    High Inertia (resistance to change)
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-1">Dependencies</label>
                  <div className="text-sm">
                    {selectedComponent.dependencies && selectedComponent.dependencies.length > 0 ? (
                      <div className="space-y-2">
                        {selectedComponent.dependencies.map((dep, idx) => (
                          <div key={idx} className="flex items-center justify-between p-2 bg-background rounded border border-border-muted">
                            <span className="text-foreground-secondary">{dep}</span>
                            <button
                              onClick={() => {
                                const newDeps = selectedComponent.dependencies?.filter((_, i) => i !== idx) || [];
                                updateComponent({ dependencies: newDeps });
                              }}
                              className="text-red-400 hover:text-red-300 text-xs"
                            >
                              ✕ Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-foreground-tertiary italic">No dependencies (use Connect mode)</p>
                    )}
                  </div>
                </div>

                <button
                  onClick={deleteComponent}
                  className="w-full px-4 py-2 bg-red-900/20 text-red-400 rounded hover:bg-red-900/30 transition-colors"
                >
                  Delete Component
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center text-foreground-tertiary py-12">
              <p className="mb-2">No component selected</p>
              <p className="text-sm">Click a component to edit its properties</p>
            </div>
          )}
        </div>
      </div>

      {/* Export modal */}
      {showExport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-2xl font-bold text-accent">Export Wardley Map</h3>
              <button
                onClick={() => setShowHelp('export')}
                className="text-foreground-tertiary hover:text-accent transition-colors"
                title="Learn how to use this feature"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </button>
            </div>

            <div className="flex gap-2 mb-4">
              <button
                onClick={() => setExportFormat('markdown')}
                className={`px-4 py-2 rounded ${
                  exportFormat === 'markdown'
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-surface text-foreground hover:bg-surfaceer'
                }`}
              >
                Markdown
              </button>
              <button
                onClick={() => setExportFormat('json')}
                className={`px-4 py-2 rounded ${
                  exportFormat === 'json'
                    ? 'bg-accent text-accent-foreground'
                    : 'bg-surface text-foreground hover:bg-surfaceer'
                }`}
              >
                JSON
              </button>
              <button
                onClick={() => {
                  handleExportPNG();
                  setShowExport(false);
                }}
                className="px-4 py-2 rounded bg-surface text-foreground hover:bg-surfaceer"
              >
                PNG Image
              </button>
            </div>

            <div className="bg-surface rounded p-4 mb-4 max-h-96 overflow-auto">
              <pre className="text-foreground text-sm whitespace-pre-wrap break-words">
                {exportFormat === 'markdown' ? generateMarkdown() : generateJSON()}
              </pre>
            </div>

            <div className="flex gap-2">
              <button
                onClick={handleExport}
                className="flex-1 px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover transition-colors"
              >
                Download File
              </button>
              <button
                onClick={() => {
                  const content = exportFormat === 'markdown' ? generateMarkdown() : generateJSON();
                  navigator.clipboard.writeText(content);
                  alert('Copied to clipboard!');
                }}
                className="flex-1 px-4 py-2 bg-surface text-foreground rounded hover:bg-surfaceer transition-colors"
              >
                Copy to Clipboard
              </button>
              <button
                onClick={() => setShowExport(false)}
                className="px-4 py-2 bg-surface text-foreground rounded hover:bg-surfaceer transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import modal */}
      {showImport && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg p-6 max-w-2xl w-full">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-2xl font-bold text-accent">Import Wardley Map</h3>
              <button
                onClick={() => setShowHelp('import')}
                className="text-foreground-tertiary hover:text-accent transition-colors"
                title="Learn how to use this feature"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <p className="text-foreground-secondary text-sm mb-4">
              Paste JSON export from a previous map to load it.
            </p>

            <textarea
              value={importText}
              onChange={(e) => setImportText(e.target.value)}
              className="w-full px-3 py-2 bg-surface rounded border border-border text-foreground focus:border-accent outline-none h-64 font-mono text-sm"
              placeholder='{"title": "My Map", "components": [...], ...}'
            />

            <div className="flex gap-2 mt-4">
              <button
                onClick={handleImport}
                className="flex-1 px-4 py-2 bg-accent text-accent-foreground rounded hover:bg-accent-hover transition-colors"
                disabled={!importText}
              >
                Import
              </button>
              <button
                onClick={() => {
                  setShowImport(false);
                  setImportText('');
                }}
                className="px-4 py-2 bg-surface text-foreground rounded hover:bg-surfaceer transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Examples modal */}
      {showExamples && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg p-6 max-w-xl w-full">
            <div className="flex items-center gap-2 mb-4">
              <h3 className="text-2xl font-bold text-accent">Load Example Map</h3>
              <button
                onClick={() => setShowHelp('examples')}
                className="text-foreground-tertiary hover:text-accent transition-colors"
                title="Learn how to use this feature"
              >
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </button>
            </div>
            <p className="text-foreground-secondary text-sm mb-4">
              Start from an example map to see how Wardley mapping works.
            </p>

            <div className="space-y-3">
              <button
                onClick={() => loadExample('platform-engineering')}
                className="w-full text-left p-4 bg-surface rounded-lg hover:bg-surfaceer transition-colors border border-border"
              >
                <h4 className="font-semibold text-foreground mb-1">Platform Engineering Evolution</h4>
                <p className="text-sm text-foreground-tertiary">
                  Custom scripts → Self-service platforms (5 components)
                </p>
              </button>

              <button
                onClick={() => loadExample('observability')}
                className="w-full text-left p-4 bg-surface rounded-lg hover:bg-surfaceer transition-colors border border-border"
              >
                <h4 className="font-semibold text-foreground mb-1">Observability Stack</h4>
                <p className="text-sm text-foreground-tertiary">
                  Monitoring and alerting evolution (4 components)
                </p>
              </button>

              <button
                onClick={() => loadExample('authentication')}
                className="w-full text-left p-4 bg-surface rounded-lg hover:bg-surfaceer transition-colors border border-border"
              >
                <h4 className="font-semibold text-foreground mb-1">Authentication & Authorization</h4>
                <p className="text-sm text-foreground-tertiary">
                  From custom auth to SSO (4 components)
                </p>
              </button>
            </div>

            <button
              onClick={() => setShowExamples(false)}
              className="w-full mt-4 px-4 py-2 bg-surface text-foreground rounded hover:bg-surfaceer transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Context Menu */}
      {contextMenu && (
        <div
          className="fixed bg-surface border border-border rounded-lg shadow-xl py-1 z-50 min-w-[180px]"
          style={{
            left: `${contextMenu.x}px`,
            top: `${contextMenu.y}px`,
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {contextMenu.componentId ? (
            <>
              <button
                onClick={handleCut}
                className="w-full text-left px-4 py-2 hover:bg-accent-muted text-foreground transition-colors flex items-center gap-2"
              >
                <span>✂️</span>
                <span>Cut</span>
                <span className="ml-auto text-xs text-foreground-tertiary">Ctrl+X</span>
              </button>
              <button
                onClick={handleCopy}
                className="w-full text-left px-4 py-2 hover:bg-accent-muted text-foreground transition-colors flex items-center gap-2"
              >
                <span>📋</span>
                <span>Copy</span>
                <span className="ml-auto text-xs text-foreground-tertiary">Ctrl+C</span>
              </button>
              <hr className="my-1 border-border-muted" />
              <button
                onClick={handleDeleteFromContext}
                className="w-full text-left px-4 py-2 hover:bg-red-900/20 text-red-400 transition-colors flex items-center gap-2"
              >
                <span>🗑️</span>
                <span>Delete</span>
                <span className="ml-auto text-xs text-foreground-tertiary">Del</span>
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handlePaste}
                disabled={!clipboard}
                className={`w-full text-left px-4 py-2 transition-colors flex items-center gap-2 ${
                  clipboard
                    ? 'hover:bg-accent-muted text-foreground'
                    : 'text-foreground-muted cursor-not-allowed'
                }`}
              >
                <span>📄</span>
                <span>Paste</span>
                <span className="ml-auto text-xs text-foreground-tertiary">Ctrl+V</span>
              </button>
              <hr className="my-1 border-border-muted" />
              <button
                onClick={handleCopyAsImage}
                className="w-full text-left px-4 py-2 hover:bg-accent-muted text-foreground transition-colors flex items-center gap-2"
              >
                <span>🖼️</span>
                <span>Copy as Image</span>
              </button>
            </>
          )}
        </div>
      )}

      {/* Help Dialog */}
      {showHelp && HELP_CONTENT[showHelp] && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-background rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-auto">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-2xl font-bold text-accent">{HELP_CONTENT[showHelp].title}</h3>
              <button
                onClick={() => setShowHelp(null)}
                className="text-foreground-tertiary hover:text-accent transition-colors"
                title="Close"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="prose prose-invert prose-orange max-w-none">
              {HELP_CONTENT[showHelp].content.split('\n').map((paragraph, idx) => {
                if (paragraph.trim() === '') return null;

                // Handle bold markdown
                const parts = paragraph.split(/(\*\*.*?\*\*)/g);
                return (
                  <p key={idx} className="text-foreground-secondary mb-3 leading-relaxed">
                    {parts.map((part, i) => {
                      if (part.startsWith('**') && part.endsWith('**')) {
                        return <strong key={i} className="text-foreground font-semibold">{part.slice(2, -2)}</strong>;
                      }
                      return part;
                    })}
                  </p>
                );
              })}
            </div>

            <div className="flex justify-end mt-6">
              <button
                onClick={() => setShowHelp(null)}
                className="px-6 py-2 bg-accent text-accent-foreground font-semibold rounded-lg hover:bg-accent-hover transition-colors"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
